import { Navigate, useNavigate, useParams } from 'react-router';
import { Banner, Button, EmptyState } from '../ds/components';
import { usePermissions, useWorkspace, type ToolKey } from '../lib/session';
import { useAccessRequest } from './access';
import { emptyTexts, isToolKey, lockedPermissionHeadlines, lockedTexts, moduleLabels } from './navigation';
import styles from './ModulePages.module.css';

type EmptyKey = keyof typeof emptyTexts;

/** Designed empty state of a module that is not built yet (Shell 14 pattern). */
export function ModuleEmptyPage({ module }: { module: EmptyKey }) {
  const navigate = useNavigate();
  const text = emptyTexts[module];
  return (
    <div className={styles.stage}>
      <EmptyState
        eyebrow={text.eyebrow}
        title={text.headline}
        description={text.description}
        actions={
          <>
            <Button onClick={() => navigate(text.primary.href)}>{text.primary.label}</Button>
            <Button variant="secondary" onClick={() => navigate(text.secondary.href)}>
              {text.secondary.label}
            </Button>
          </>
        }
      />
    </div>
  );
}

/** Shell 13: the tool exists but the user has no access; "Zugriff beantragen" creates an approval task. */
export function LockedPage({ tool }: { tool: ToolKey }) {
  const navigate = useNavigate();
  const workspace = useWorkspace();
  const access = useAccessRequest();
  const admin = workspace.approvalRecipientName;
  const text = lockedTexts[tool];
  const headline =
    workspace.tools[tool] === 'no_permission'
      ? (lockedPermissionHeadlines[tool] ?? text.headline)
      : text.headline;
  const description = text.description.replace('{admin}', admin ?? 'Ihr Org-Admin');
  const footnote = admin
    ? `Der Antrag erscheint als Freigabe-Task bei ${admin}.`
    : 'Der Antrag erscheint als Freigabe-Task bei Ihrem Org-Admin.';
  return (
    <div className={styles.stage}>
      <EmptyState
        eyebrow={moduleLabels[tool]}
        title={headline}
        description={
          <>
            {description}
            {access.error ? (
              <span className={styles.banner}>
                <Banner tone="danger">{access.error}</Banner>
              </span>
            ) : null}
            {access.done ? (
              <span className={styles.banner}>
                <Banner tone="success">Ihr Antrag wurde gesendet.</Banner>
              </span>
            ) : null}
          </>
        }
        actions={
          <>
            <Button
              loading={access.pending}
              disabled={access.done}
              onClick={() => void access.submit({ kind: 'tool', tool })}
            >
              Zugriff beantragen
            </Button>
            <Button variant="secondary" onClick={() => navigate('/tasks')}>
              Zurück zu Tasks
            </Button>
          </>
        }
        footnote={footnote}
      />
    </div>
  );
}

/** `/locked/:tool` — the locked page by route; an unknown tool goes home. */
export function LockedRoute() {
  const params = useParams();
  const tool = params['tool'] ?? '';
  if (!isToolKey(tool)) return <Navigate to="/tasks" replace />;
  return <LockedPage tool={tool} />;
}

/** A tool route: renders the locked page unless the session grants the tool. */
export function ToolPage({ tool }: { tool: Exclude<ToolKey, 'pkv'> }) {
  const permissions = usePermissions();
  if (permissions.tool(tool) !== 'ok') return <LockedPage tool={tool} />;
  return <ModuleEmptyPage module={tool} />;
}

/** A main-nav module without a tool gate (contacts, calendar, properties, controlling). */
export function ModulePage({ module }: { module: Exclude<EmptyKey, ToolKey> }) {
  return <ModuleEmptyPage module={module} />;
}
